import {
  Flexbox,
  Heading,
  Button,
  Icon,
  Divider,
  Card,
  Modal,
  EmptyState,
  Table,
  Select,
} from '@components/common'
import { PRODUCTION_UNITS_FORM_ID } from '@constants'
import { useParams } from 'react-router-dom'
import { isNullOrUndefined } from '@utils/basic'
import {
  useGetAssetQuery,
  useGetFiscalPeriodInfoQuery,
} from '@api'
import { FormProvider } from 'react-hook-form'
import Template from '@components/layout/Template'
import usePermission from '@components/Auth/Permissions/usePermission'
import { useCallback, useEffect, useState } from 'react'

// Production Units components
import ProductionUnitsTable from './ProductionUnits/ProductionUnitsTable'
import ProductionUnitsForm from './ProductionUnits/ProductionUnitsForm'
import ProductionUnitsDeleteForm from './ProductionUnits/ProductionUnitsDeleteForm'
import ProductionUnitsSkippedPeriodModal from './ProductionUnits/ProductionUnitsSkippedPeriodModal'

import { useProductionUnits } from './ProductionUnits/useProductionUnits'

const AssetProductionUnitsWrapper = () => {
  const urlParams = useParams()

  const params = {
    assetID: urlParams.assetID,
    assetCompanyID: parseInt(urlParams.companyID),
  }

  let EditingEnabled = false

  const { data: assetDetails } = useGetAssetQuery({
    assetCompanyID: parseInt(urlParams.companyID),
    assetID: urlParams.assetID,
  })

  if (!isNullOrUndefined(assetDetails)) {
    EditingEnabled =
      assetDetails.AS_DIS_CD === 'D' || assetDetails.AS_DIS_CD === 'P' ? true : false
  }

  const { canEditSettings: canEdit } = usePermission()

  const { data: fiscalPeriodInfo } = useGetFiscalPeriodInfoQuery({
    assetCompanyID: params.assetCompanyID,
  })

  const {
    productionUnits,
    allProductionUnits,
    isLoadingUnits,
    availableYears,
    selectedYear,
    yearOptions,
    handleChangeYear,
    isFormOpen,
    isDeleteModalOpen,
    formType,
    selectedUnit,
    formMethods,
    openAddForm,
    openEditForm,
    openDeleteModal,
    closeForm,
    handleSubmit,
    handleDelete,
    isSubmitting,
    isDeleting,
    isFormValid,
    isSkippedPeriodModalOpen,
    closeSkippedPeriodModal,
    handleConfirmedSubmit,
    setFirstAvailablePeriodCallback,
    setFirstAvailableYearCallback,
  } = useProductionUnits({
    params: {
      assetCompanyID: params.assetCompanyID,
      assetID: params.assetID,
    },
  })

  const [
    firstAvailablePeriodForCurrentYear,
    setFirstAvailablePeriodForCurrentYear,
  ] = useState<number | null>(null)
  const [firstAvailableYear, setFirstAvailableYear] = useState<number | null>(null)

  const getFirstAvailablePeriod = useCallback((): number | null => {
    return firstAvailablePeriodForCurrentYear
  }, [firstAvailablePeriodForCurrentYear])

  const getFirstAvailableYear = useCallback((): number | null => {
    return firstAvailableYear
  }, [firstAvailableYear])

  useEffect(() => {
    if (setFirstAvailablePeriodCallback) {
      setFirstAvailablePeriodCallback(getFirstAvailablePeriod)
    }
  }, [setFirstAvailablePeriodCallback, getFirstAvailablePeriod])

  useEffect(() => {
    if (setFirstAvailableYearCallback) {
      setFirstAvailableYearCallback(getFirstAvailableYear)
    }
  }, [setFirstAvailableYearCallback, getFirstAvailableYear])

  if (isLoadingUnits) return <></>

  return (
    <>
      <Flexbox alignItems="center" justifyContent="space-between">
        <div>
          <Heading type="h2">Production Units</Heading>
        </div>
      </Flexbox>
      <Divider spacing="default" />

      {allProductionUnits.length > 0 ? (
        <Template maxWidth="compact">
          <Card spacing="none" overflow="hidden">
            <Table.Actions>
              <Flexbox justifyContent="space-between">
                <Flexbox alignItems="flex-start" flexDirection="column">
                  {yearOptions.length > 0 && (
                    <>
                      <Select
                        value={{
                          label: `Fiscal year: ${selectedYear}`,
                          value: selectedYear,
                        }}
                        options={yearOptions}
                        onChange={handleChangeYear}
                        width={'164px'}
                        appearance="clear"
                      />
                    </>
                  )}
                </Flexbox>

                {canEdit && !EditingEnabled && (
                  <Button appearance="primary" onClick={openAddForm}>
                    Add New Record
                  </Button>
                )}
              </Flexbox>
            </Table.Actions>
            <ProductionUnitsTable
              productionUnits={productionUnits}
              allProductionUnits={allProductionUnits}
              onEdit={openEditForm}
              onDelete={openDeleteModal}
              readonly={EditingEnabled}
              canEdit={canEdit}
            />
          </Card>
        </Template>
      ) : (
        <Card>
          <EmptyState
            heading={`No production units records have been added to this asset.`}
            primaryAction={
              <>
                {canEdit && !EditingEnabled && (
                  <Button appearance="primary" onClick={openAddForm}>
                    Add New Record
                  </Button>
                )}
              </>
            }
          />
        </Card>
      )}

      <Modal
        isOpen={isFormOpen}
        title={`${formType === 'add' ? 'Add' : 'Edit'} Production Units Record`}
        form={PRODUCTION_UNITS_FORM_ID}
        close={closeForm}
        actionLabel={formType === 'add' ? 'Add Record' : 'Save Changes'}
        isActionLoading={isSubmitting}
        width={640}
        actionDisabled={!isFormValid || !canEdit}
      >
        <FormProvider {...formMethods}>
          <ProductionUnitsForm
            formId={PRODUCTION_UNITS_FORM_ID}
            type={formType}
            onSubmit={handleSubmit}
            initialValues={selectedUnit || {}}
            assetCompanyID={params.assetCompanyID}
            assetID={params.assetID}
            disabled={!canEdit}
            readonly={EditingEnabled}
            existingProductionUnits={allProductionUnits}
            onFirstAvailablePeriodChange={setFirstAvailablePeriodForCurrentYear}
            onFirstAvailableYearChange={setFirstAvailableYear}
          />
        </FormProvider>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        title="Delete Production Units Record"
        close={closeForm}
        actionLabel="Delete Record"
        isActionLoading={isDeleting}
        width={640}
        actionDisabled={!canEdit}
        intent="danger"
        action={handleDelete}
      >
        {selectedUnit && <ProductionUnitsDeleteForm unit={selectedUnit} />}
      </Modal>

      <ProductionUnitsSkippedPeriodModal
        isOpen={isSkippedPeriodModalOpen}
        onClose={closeSkippedPeriodModal}
        onConfirm={handleConfirmedSubmit}
        isLoading={isSubmitting}
      />
    </>
  )
}

export default AssetProductionUnitsWrapper
